import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

const COLLAPSED_KEY = 'bf.sidebar.collapsed';

interface ShellContextProps {
	/** Barra lateral en modo de íconos (solo escritorio). */
	collapsed: boolean;
	setCollapsed: (collapsed: boolean) => void;
	toggleCollapsed: () => void;
	/** Menú de navegación abierto en pantallas angostas. */
	mobileOpen: boolean;
	setMobileOpen: (open: boolean) => void;
}

const ShellContext = createContext<ShellContextProps | undefined>(undefined);

const readCollapsed = () => {
	try {
		return localStorage.getItem(COLLAPSED_KEY) === '1';
	} catch {
		return false;
	}
};

export const ShellProvider = ({ children }: { children: ReactNode }) => {
	const [collapsed, setCollapsedState] = useState<boolean>(readCollapsed);
	const [mobileOpen, setMobileOpen] = useState(false);

	const setCollapsed = useCallback((value: boolean) => {
		setCollapsedState(value);
		try {
			localStorage.setItem(COLLAPSED_KEY, value ? '1' : '0');
		} catch {
			// sin almacenamiento la barra queda como esté durante la sesión de la página
		}
	}, []);

	const toggleCollapsed = useCallback(() => setCollapsed(!collapsed), [collapsed, setCollapsed]);

	const value = useMemo(
		() => ({ collapsed, setCollapsed, toggleCollapsed, mobileOpen, setMobileOpen }),
		[collapsed, setCollapsed, toggleCollapsed, mobileOpen]
	);

	return <ShellContext.Provider value={value}>{children}</ShellContext.Provider>;
};

export const useShell = () => {
	const context = useContext(ShellContext);
	if (!context) throw new Error('useShell debe usarse dentro de ShellProvider');
	return context;
};
